import { RefObject, useEffect, useState } from 'react'

/** how close to the screen a section has to come before it loads anything */
export const NEAR_VIEWPORT_MARGIN_PX = 200

export type NearViewport = {
  /** near the screen right now (a preview may play) */
  inView: boolean
  /** has been near the screen at least once (posters and code may load) */
  seen: boolean
}

/**
 * Like a lazy image, for a whole section. One state object: an observer
 * callback is not batched in React 17. Without IntersectionObserver the
 * section counts as on screen.
 */
export const useNearViewport = (
  ref: RefObject<HTMLElement>,
  marginPx = NEAR_VIEWPORT_MARGIN_PX
): NearViewport => {
  const [view, setView] = useState<NearViewport>({
    inView: false,
    seen: false,
  })
  useEffect(() => {
    const el = ref.current
    if (!el || typeof IntersectionObserver === 'undefined') {
      setView({ inView: true, seen: true })
      return
    }
    const observer = new IntersectionObserver(
      ([entry]) => {
        const hit = !!entry?.isIntersecting
        setView(v => ({ inView: hit, seen: v.seen || hit }))
      },
      { rootMargin: `${marginPx}px 0px` }
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [ref, marginPx])
  return view
}
